
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import ReactFlow, {
  addEdge,
  Background,
  Controls,
  Connection,
  Edge,
  Node,
  Panel,
  MarkerType,
  OnNodesChange,
  OnEdgesChange,
  applyNodeChanges,
  applyEdgeChanges,
  ConnectionMode,
  ReactFlowInstance,
  ReactFlowProvider,
  updateEdge,
  MiniMap
} from 'reactflow';
import { v4 as uuidv4 } from 'uuid';
import {
  ArrowLeft, Undo2, Redo2, LayoutGrid, Settings,
  Upload, Download, Save, ZoomIn, ZoomOut, Maximize,
  Plus, Minus, AlertTriangle, Search, X, Link2, GitBranch
} from 'lucide-react';

import GroupNode from './GroupNode';
import CustomEdge from './CustomEdge';
import NodeInspector from './NodeInspector';
import SaveDialog from './SaveDialog';
import RegexConnectModal from './RegexConnectModal';
import PrefixRestructureModal from './PrefixRestructureModal';
import { NodeData, GroupData, MemberFetcher, HistoryState, InnerProps, GroupEditorProps } from './types';
import { DiGraph, createEmptyNode, groupFromNode, layoutNodes } from './utils';
import { buildPrefixRestructure, type PrefixLengthStep } from './prefixRestructure';
import { EditorContextProvider } from './EditorContext';

// --- Undo/Redo Hook ---

const useUndoRedo = () => {
  const [past, setPast] = useState<HistoryState[]>([]);
  const [future, setFuture] = useState<HistoryState[]>([]);

  const takeSnapshot = useCallback((nodes: Node<NodeData>[], edges: Edge[]) => {
    setPast((old) => {
      // Limit history to 50 steps to prevent memory issues
      const newPast = old.length > 50 ? old.slice(1) : old;
      return [...newPast, { nodes, edges }];
    });
    setFuture([]);
  }, []);

  const undo = useCallback((currentNodes: Node<NodeData>[], currentEdges: Edge[]) => {
    if (past.length === 0) return null;
    
    const previous = past[past.length - 1];
    const newPast = past.slice(0, past.length - 1);
    
    setPast(newPast);
    setFuture((old) => [{ nodes: currentNodes, edges: currentEdges }, ...old]);
    
    return previous;
  }, [past]);

  const redo = useCallback((currentNodes: Node<NodeData>[], currentEdges: Edge[]) => {
    if (future.length === 0) return null;
    
    const next = future[0];
    const newFuture = future.slice(1);
    
    setFuture(newFuture);
    setPast((old) => [...old, { nodes: currentNodes, edges: currentEdges }]);
    
    return next;
  }, [future]);

  return { takeSnapshot, undo, redo, canUndo: past.length > 0, canRedo: future.length > 0 };
};

const InnerImplementation: React.FC<InnerProps> = ({ 
  groupData, 
  nodes: initialNodes, 
  edges: initialEdges = [], 
  onSave, 
  onExport, 
  onBack, 
  isValidated, 
  enableForce = true,
  onFetchMembers,
  icons
}) => {
  const [nodes, setNodes] = useState<Node<NodeData>[]>(initialNodes);
  const [edges, setEdges] = useState<Edge[]>(initialEdges);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [editingLocked, setEditingLocked] = useState(false);
  const [canvasLocked, setCanvasLocked] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [savingBusy, setSavingBusy] = useState(false);
  const [regexModalOpen, setRegexModalOpen] = useState(false);
  const [prefixModalOpen, setPrefixModalOpen] = useState(false);
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);

  const nodeTypes = useMemo(() => ({ group: GroupNode }), []);
  const edgeTypes = useMemo(() => ({ base: CustomEdge }), []);

  // Undo/Redo State
  const { takeSnapshot, undo, redo, canUndo, canRedo } = useUndoRedo();

  // Search State
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMatches, setSearchMatches] = useState<string[]>([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const connectingNodeId = useRef<string | null>(null);
  const edgeUpdateSuccessful = useRef<boolean>(true);

  // Undo/Redo Handlers
  const handleUndo = useCallback(() => {
    const previous = undo(nodes, edges);
    if (previous) {
      setNodes(previous.nodes);
      setEdges(previous.edges);
    }
  }, [undo, nodes, edges]);

  const handleRedo = useCallback(() => {
    const next = redo(nodes, edges);
    if (next) {
      setNodes(next.nodes);
      setEdges(next.edges);
    }
  }, [redo, nodes, edges]);

  // Keyboard Shortcuts for Undo/Redo
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore inputs
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        if (e.shiftKey) {
          handleRedo();
        } else {
          handleUndo();
        }
        e.preventDefault();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') {
        handleRedo();
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo]);


  const evaluateGraph = useCallback((currentNodes: Node<NodeData>[], currentEdges: Edge[]) => {
    const graph = new DiGraph();
    currentNodes.forEach(n => graph.addVertex(n.id));
    currentEdges.forEach(e => graph.addEdge(e.source, e.target));

    const cycles = graph.findCycles();
    const cycleNodes = new Set(cycles.flat());
    const isAcyclic = cycles.length === 0;

    // Topological validation
    const targets = new Set(currentEdges.map(e => e.target));
    const topologicalRoots = currentNodes.filter(n => !targets.has(n.id));
    const isConnected = topologicalRoots.length === 1;

    // Determine Validation State
    let error: string | null = null;
    if (!isAcyclic) {
      error = "Cycles detected in hierarchy.";
    } else if (currentNodes.length > 0 && !isConnected) {
      error = `Graph disconnected: Found ${topologicalRoots.length} root-like nodes. Ensure all groups are connected.`;
    }
    setValidationError(error);

    // Preserve object identity for unchanged nodes/edges so the memoized
    // node/edge components skip re-rendering — critical with 1000+ nodes.
    let nodesChanged = false;
    const updatedNodes = currentNodes.map(node => {
      const isDesignatedRoot = node.id === groupData.id;
      const inCycle = cycleNodes.has(node.id);
      if (
        node.deletable === !isDesignatedRoot &&
        node.data.inCycle === inCycle &&
        node.data.isRootNode === isDesignatedRoot
      ) {
        return node;
      }
      nodesChanged = true;
      return {
        ...node,
        deletable: !isDesignatedRoot,
        data: {
          ...node.data,
          inCycle,
          isRootNode: isDesignatedRoot
        }
      };
    });

    let edgesChanged = false;
    const updatedEdges = currentEdges.map(edge => {
      const inCycle = cycleNodes.has(edge.source) && cycleNodes.has(edge.target);
      if (edge.animated === inCycle && edge.data?.inCycle === inCycle) {
        return edge;
      }
      edgesChanged = true;
      return {
        ...edge,
        animated: inCycle,
        data: {
          ...edge.data,
          inCycle: inCycle
        }
      };
    });

    if (nodesChanged) setNodes(updatedNodes);
    if (edgesChanged) setEdges(updatedEdges);
  }, [groupData.id]);

  const onNodesChange: OnNodesChange = useCallback(
    (changes) => {
      setNodes((nds) => applyNodeChanges(changes, nds));
    },
    []
  );

  const onEdgesChange: OnEdgesChange = useCallback(
    (changes) => {
      setEdges((eds) => applyEdgeChanges(changes, eds));
    },
    []
  );

  // Evaluate graph whenever topology changes
  useEffect(() => {
     evaluateGraph(nodes, edges);
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    nodes.length, 
    edges.length, 
    edges.map(e => e.source + e.target).join(',')
  ]);

  // Handle Search Input Keydown
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchMatches([]);
      setCurrentMatchIndex(0);
      return;
    }
    const matches = nodes.filter(n => 
      n.data.name.toLowerCase().includes(query.toLowerCase()) ||
      n.data.description.toLowerCase().includes(query.toLowerCase())
    ).map(n => n.id);
    
    setSearchMatches(matches);
    setCurrentMatchIndex(0);
    
    if (matches.length > 0) {
      focusNode(matches[0]);
    }
  };

  const focusNode = (nodeId: string) => {
    if (!reactFlowInstance) return;
    const node = reactFlowInstance.getNode(nodeId);
    if (!node) return;

    setSelectedNodeId(nodeId);
    reactFlowInstance.setCenter(
      node.position.x + (node.width || 200) / 2, 
      node.position.y + (node.height || 100) / 2, 
      { zoom: 1.2, duration: 800 }
    );
  };

  const nextMatch = () => {
    if (searchMatches.length === 0) return;
    const nextIndex = (currentMatchIndex + 1) % searchMatches.length;
    setCurrentMatchIndex(nextIndex);
    focusNode(searchMatches[nextIndex]);
  };

  const handleErrorClick = () => {
    if (!validationError) return;
    if (validationError.includes("Disconnected") || validationError.includes("disconnected")) {
      const targets = new Set(edges.map(e => e.target));
      const orphans = nodes.filter(n => !targets.has(n.id) && n.id !== groupData.id);
      if (orphans.length > 0) focusNode(orphans[0].id);
    } else if (validationError.includes("Cycles") || validationError.includes("Cycle")) {
      const cycleNode = nodes.find(n => n.data.inCycle);
      if (cycleNode) focusNode(cycleNode.id);
    }
  };

  useEffect(() => {
    if (selectedNodeId && reactFlowInstance) {
       const node = reactFlowInstance.getNode(selectedNodeId);
       if(node) {
          reactFlowInstance.setCenter(
            node.position.x + (node.width || 200) / 2, 
            node.position.y + (node.height || 100) / 2, 
            { zoom: 1.2, duration: 600 }
          );
       }
    }
  }, [selectedNodeId, reactFlowInstance]);

  const onConnect = useCallback((params: Connection) => {
    if (editingLocked) return;
    const hasParent = edges.some(e => e.target === params.target);
    if (hasParent) {
      alert("A group can only have one parent.");
      return;
    }
    // Snapshot before connecting
    takeSnapshot(nodes, edges);
    setEdges((eds) => addEdge({ 
      ...params, 
      type: 'base',
      markerEnd: { type: MarkerType.ArrowClosed }
    }, eds));
  }, [edges, editingLocked, nodes, takeSnapshot]);

  const onConnectStart = useCallback((_: any, { nodeId }: { nodeId: string | null }) => {
    connectingNodeId.current = nodeId;
  }, []);

  const onConnectEnd = useCallback((event: any) => {
    if (!connectingNodeId.current || editingLocked) return;
    const targetIsPane = event.target.classList.contains('react-flow__pane');
    if (targetIsPane && reactFlowInstance && wrapperRef.current) {
      const { top, left } = wrapperRef.current.getBoundingClientRect();
      const id = uuidv4();
      const position = reactFlowInstance.project({
        x: event.clientX - left,
        y: event.clientY - top,
      });
      
      // Snapshot before creating new node
      takeSnapshot(nodes, edges);

      const newNode = createEmptyNode(id, position, false);
      const newEdge: Edge = {
        id: `e-${connectingNodeId.current}-${id}`,
        source: connectingNodeId.current,
        target: id,
        type: 'base',
        markerEnd: { type: MarkerType.ArrowClosed }
      };
      setNodes((nds) => nds.concat(newNode));
      setEdges((eds) => eds.concat(newEdge));
      setSelectedNodeId(id);
    }
    connectingNodeId.current = null;
  }, [reactFlowInstance, editingLocked, nodes, edges, takeSnapshot]);

  const onEdgeUpdateStart = useCallback((_: any, edge: Edge) => {
    edgeUpdateSuccessful.current = false;
  }, []);

  const onEdgeUpdate = useCallback((oldEdge: Edge, newConnection: Connection) => {
    edgeUpdateSuccessful.current = true;
    takeSnapshot(nodes, edges);
    setEdges((els) => updateEdge(oldEdge, newConnection, els));
  }, [nodes, edges, takeSnapshot]);

  const onEdgeUpdateEnd = useCallback((_: any, edge: Edge) => {
    if (!edgeUpdateSuccessful.current) {
      takeSnapshot(nodes, edges);
      setEdges((eds) => eds.filter((e) => e.id !== edge.id));
    }
    edgeUpdateSuccessful.current = true;
  }, [nodes, edges, takeSnapshot]);

  const onNodeClick = useCallback((_: any, node: Node) => {
    setSelectedNodeId(node.id);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNodeId(null);
  }, []);

  const onUpdateNode = (nodeId: string, newData: Partial<NodeData>) => {
    takeSnapshot(nodes, edges);
    setNodes(nds => nds.map(n => n.id === nodeId ? { ...n, data: { ...n.data, ...newData } } : n));
  };

  const handleRemoveEdge = useCallback((edgeId: string) => {
    takeSnapshot(nodes, edges);
    setEdges(eds => eds.filter(e => e.id !== edgeId));
  }, [nodes, edges, takeSnapshot]);

  // Handler to snapshot before nodes/edges are deleted via keyboard
  const onNodesDelete = useCallback((deleted: Node[]) => {
    if (deleted.length > 0) {
      takeSnapshot(nodes, edges);
    }
  }, [nodes, edges, takeSnapshot]);

  const onEdgesDelete = useCallback((deleted: Edge[]) => {
    if (deleted.length > 0) {
      takeSnapshot(nodes, edges);
    }
  }, [nodes, edges, takeSnapshot]);

  const onNodeDragStart = useCallback(() => {
    takeSnapshot(nodes, edges);
  }, [nodes, edges, takeSnapshot]);


  const handleAutoLayout = useCallback(() => {
    if (nodes.length === 0) return;
    takeSnapshot(nodes, edges);
    setNodes(layoutNodes(nodes, edges, groupData.id));
    // Fit the view once React has committed the new positions.
    setTimeout(() => reactFlowInstance?.fitView({ duration: 800 }), 50);
  }, [nodes, edges, groupData.id, takeSnapshot, reactFlowInstance]);

  // Ancestors of a node (walked against edge direction) — used to keep regex
  // re-parenting acyclic.
  const getAncestors = useCallback((nodeId: string): Set<string> => {
    const parentsOf = new Map<string, string[]>();
    edges.forEach(e => {
      const list = parentsOf.get(e.target) || [];
      list.push(e.source);
      parentsOf.set(e.target, list);
    });
    const ancestors = new Set<string>();
    const stack = [nodeId];
    while (stack.length) {
      const current = stack.pop()!;
      for (const parent of parentsOf.get(current) || []) {
        if (!ancestors.has(parent)) {
          ancestors.add(parent);
          stack.push(parent);
        }
      }
    }
    return ancestors;
  }, [edges]);

  // Nodes that may be re-parented under the selected node: everything except the
  // selected node itself, the root, and the selected node's ancestors.
  const regexCandidates = useMemo(() => {
    if (!selectedNodeId) return [];
    const ancestors = getAncestors(selectedNodeId);
    return nodes.filter(n =>
      n.id !== selectedNodeId &&
      n.id !== groupData.id &&
      !ancestors.has(n.id)
    );
  }, [selectedNodeId, nodes, groupData.id, getAncestors]);

  const prefixCandidates = useMemo(() => {
    if (!selectedNodeId) return [];

    const childrenOf = new Map<string, string[]>();
    edges.forEach(e => {
      const children = childrenOf.get(e.source) || [];
      children.push(e.target);
      childrenOf.set(e.source, children);
    });

    const descendantIds = new Set<string>();
    const stack = [...(childrenOf.get(selectedNodeId) || [])];
    while (stack.length) {
      const current = stack.pop()!;
      if (descendantIds.has(current)) continue;
      descendantIds.add(current);
      stack.push(...(childrenOf.get(current) || []));
    }

    return nodes.filter(n => descendantIds.has(n.id));
  }, [selectedNodeId, nodes, edges]);

  const handleConnectByRegex = useCallback((targetIds: string[]) => {
    if (!selectedNodeId || targetIds.length === 0) return;
    const targetSet = new Set(targetIds);
    takeSnapshot(nodes, edges);
    setEdges(eds => {
      // Drop each target's current parent edge (one parent per group)...
      const kept = eds.filter(e => !targetSet.has(e.target));
      // ...then attach it to the selected node.
      const added: Edge[] = targetIds.map(t => ({
        id: `e-${selectedNodeId}-${t}`,
        source: selectedNodeId,
        target: t,
        type: 'base',
        markerEnd: { type: MarkerType.ArrowClosed }
      }));
      return [...kept, ...added];
    });
    setRegexModalOpen(false);
  }, [selectedNodeId, nodes, edges, takeSnapshot]);

  const handleRestructureByPrefix = useCallback((lengths: PrefixLengthStep[]) => {
    if (!selectedNodeId || lengths.length === 0) return;

    const result = buildPrefixRestructure(
      nodes,
      edges,
      selectedNodeId,
      prefixCandidates,
      lengths
    );
    if (!result) return;

    takeSnapshot(nodes, edges);
    setNodes(layoutNodes(result.nodes, result.edges, groupData.id));
    setEdges(result.edges);
    setPrefixModalOpen(false);
    setTimeout(() => reactFlowInstance?.fitView({ duration: 800 }), 50);
  }, [selectedNodeId, nodes, edges, prefixCandidates, takeSnapshot, groupData.id, reactFlowInstance]);

  const handleSaveSubmit = (reason: string, message: string) => {
    setSavingBusy(true);
    const rootNode = nodes.find(n => n.id === groupData.id) || nodes[0];
    const data = {
      ...groupData,
      id: rootNode.id,
      reason,
      message,
      nodes: nodes.map(n => groupFromNode(n, edges))
    };
    setTimeout(() => {
      onSave(data);
      setSavingBusy(false);
      setSaveDialogOpen(false);
    }, 800);
  };

  const selectedNode = nodes.find(n => n.id === selectedNodeId) || null;
  const isSaveDisabled = !!validationError || !isValidated || savingBusy;

  return (
    <div className="flex flex-col h-full bg-white text-slate-900 font-sans">
      <EditorContextProvider 
        onFetchMembers={onFetchMembers} 
        iconMap={icons} 
        onRemoveEdge={handleRemoveEdge} 
        onUpdateNode={onUpdateNode}
      >
      {/* Header Bar */}
      <header className="h-16 border-b border-slate-200 flex items-center justify-between px-4 bg-white z-20 shadow-sm shrink-0 relative">
        <div className="flex items-center gap-4 flex-1">
          <button 
            onClick={onBack}
            className="p-2 hover:bg-slate-100 rounded-full text-slate-500 transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          
          <div className="flex flex-col min-w-[120px]">
            <h1 className="text-lg font-bold leading-tight truncate">{groupData.name || 'Hierachy Editor'}</h1>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span className="bg-slate-100 px-1.5 py-0.5 rounded text-slate-600 font-medium">{nodes.length} Groups</span>
              <span>•</span>
              <span>{groupData.type}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1 pl-4">
          <button 
            onClick={handleUndo} 
            disabled={!canUndo}
            className={`p-2 rounded-lg transition-colors ${canUndo ? 'text-slate-600 hover:bg-slate-100' : 'text-slate-300 cursor-not-allowed'}`} 
            title="Undo (Ctrl+Z)"
          >
            <Undo2 size={18} />
          </button>
          <button 
            onClick={handleRedo} 
            disabled={!canRedo}
            className={`p-2 mr-2 rounded-lg transition-colors ${canRedo ? 'text-slate-600 hover:bg-slate-100' : 'text-slate-300 cursor-not-allowed'}`}
            title="Redo (Ctrl+Y)"
          >
            <Redo2 size={18} />
          </button>
          
          <div className="h-6 w-px bg-slate-200 mx-1 hidden sm:block" />

          <button
            onClick={handleAutoLayout}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg"
            title="Auto-arrange layout"
          >
            <LayoutGrid size={18} />
          </button>
          <button
            onClick={() => setRegexModalOpen(true)}
            disabled={!selectedNodeId || selectedNodeId === groupData.id}
            className={`p-2 rounded-lg transition-colors ${
              selectedNodeId && selectedNodeId !== groupData.id
                ? 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'
                : 'text-slate-300 cursor-not-allowed'
            }`}
            title={
              selectedNodeId && selectedNodeId !== groupData.id
                ? 'Connect matching groups by regex'
                : 'Select a group first to connect matches under it'
            }
          >
            <Link2 size={18} />
          </button>
          <button
            onClick={() => setPrefixModalOpen(true)}
            disabled={!selectedNodeId || prefixCandidates.length === 0}
            className={`p-2 rounded-lg transition-colors ${
              selectedNodeId && prefixCandidates.length > 0
                ? 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'
                : 'text-slate-300 cursor-not-allowed'
            }`}
            title={
              selectedNodeId && prefixCandidates.length > 0
                ? 'Restructure this group\'s children by prefix chunks'
                : 'Select a group with child groups first'
            }
          >
            <GitBranch size={18} />
          </button>
          <button className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg mr-2" title="Settings">
            <Settings size={18} />
          </button>
          
          <div className="h-6 w-px bg-slate-200 mx-1 hidden sm:block" />

          <button 
             onClick={() => alert('Upload clicked')}
             className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg"
          >
            <Upload size={18} />
          </button>
          <button 
             onClick={() => onExport({ id: groupData.id, nodes, edges })}
             className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg mr-4"
          >
            <Download size={18} />
          </button>

          <button
            onClick={() => setSaveDialogOpen(true)}
            disabled={isSaveDisabled}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all shadow-sm
              ${isSaveDisabled
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200' 
                : 'bg-teal-700 text-white hover:bg-teal-800'
              }
            `}
          >
            <Save size={16} />
            <span>Save</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Canvas Area */}
        <div className="flex-1 relative bg-slate-50 transition-all duration-300" ref={wrapperRef}>
          
          {/* Floating Controls Overlay */}
          <div className="absolute top-4 left-4 right-4 sm:right-auto sm:w-80 z-10 flex flex-col gap-2 pointer-events-none">
            {/* Search Bar */}
            <div className="relative w-full group pointer-events-auto shadow-md rounded-lg">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-teal-500 transition-colors">
                  <Search size={16} />
                </div>
                <input 
                  ref={searchInputRef}
                  className="w-full pl-9 pr-12 py-2.5 bg-white/95 backdrop-blur border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all"
                  placeholder="Search groups (Ctrl+F)"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      nextMatch();
                    }
                  }}
                />
                {searchMatches.length > 0 && (
                   <div className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
                      {currentMatchIndex + 1}/{searchMatches.length}
                   </div>
                )}
                {searchQuery && searchMatches.length === 0 && (
                   <div className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-300">
                     <X size={14} className="cursor-pointer hover:text-red-500" onClick={() => handleSearch('')} />
                   </div>
                )}
            </div>

            {/* Validation Error Floating Card */}
            {validationError && (
              <div 
                onClick={handleErrorClick}
                className="pointer-events-auto cursor-pointer flex items-center gap-3 px-4 py-3 bg-red-50/95 backdrop-blur text-red-700 rounded-lg border border-red-200 shadow-md animate-in fade-in slide-in-from-top-4 hover:bg-red-100 transition-colors group"
                role="alert"
                title="Click to zoom to issue"
              >
                  <div className="p-2 bg-red-100 rounded-full text-red-600 group-hover:bg-white transition-colors shrink-0">
                      <AlertTriangle size={16} className="stroke-[2.5]" />
                  </div>
                  <div className="flex-1 min-w-0">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-red-800 mb-0.5">Validation Error</h4>
                      <p className="text-xs font-medium leading-tight">{validationError}</p>
                  </div>
              </div>
            )}
          </div>

          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodesDelete={onNodesDelete}
            onEdgesDelete={onEdgesDelete}
            onNodeDragStart={onNodeDragStart}
            onConnect={onConnect}
            onConnectStart={onConnectStart}
            onConnectEnd={onConnectEnd}
            onEdgeUpdate={onEdgeUpdate}
            onEdgeUpdateStart={onEdgeUpdateStart}
            onEdgeUpdateEnd={onEdgeUpdateEnd}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            onInit={setReactFlowInstance}
            connectionMode={ConnectionMode.Loose}
            onlyRenderVisibleElements
            fitView
            snapToGrid
            snapGrid={[15, 15]}
            nodesDraggable={!canvasLocked}
            nodesConnectable={!editingLocked}
            elementsSelectable={true}
            deleteKeyCode={['Delete', 'Backspace']} 
            className="bg-slate-50"
            proOptions={{ hideAttribution: true }}
            zoomOnScroll={true}
            panOnScroll={false}
          >
            <Background color="#cbd5e1" gap={20} />
            
            <MiniMap 
              nodeColor={(n) => n.data.isRootNode ? '#0f766e' : '#e2e8f0'}
              maskColor="rgba(248, 250, 252, 0.6)"
              className="!bottom-6 !left-auto !right-6 bg-white border border-slate-200 shadow-lg rounded-lg"
              style={{ width: 120, height: 90 }}
            />

            {/* Custom Controls Positioned Bottom Left */}
            <Panel position="bottom-left" className="m-4 ml-6 mb-6 flex flex-col gap-1 bg-white p-1 rounded-lg shadow-md border border-slate-200">
               <button onClick={() => reactFlowInstance?.zoomIn()} className="p-2 hover:bg-slate-100 rounded text-slate-600"><Plus size={18}/></button>
               <button onClick={() => reactFlowInstance?.zoomOut()} className="p-2 hover:bg-slate-100 rounded text-slate-600"><Minus size={18}/></button>
               <div className="h-px bg-slate-200 my-0.5 mx-2" />
               <button onClick={() => reactFlowInstance?.fitView({ duration: 800 })} className="p-2 hover:bg-slate-100 rounded text-slate-600"><Maximize size={18}/></button>
            </Panel>
            
          </ReactFlow>
        </div>

        {/* Sidebar */}
        {selectedNodeId && (
          <div className="h-full border-l border-slate-200 bg-white shadow-xl z-20 animate-in slide-in-from-right-10 duration-200">
            <NodeInspector
              node={selectedNode}
              readOnly={editingLocked}
              onUpdateNode={onUpdateNode}
              onClose={() => setSelectedNodeId(null)}
            />
          </div>
        )}
      </div>
      
      <SaveDialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        onSubmit={handleSaveSubmit}
        saving={savingBusy}
      />

      <RegexConnectModal
        open={regexModalOpen}
        parentNode={selectedNode}
        candidates={regexCandidates}
        onClose={() => setRegexModalOpen(false)}
        onConfirm={handleConnectByRegex}
      />

      <PrefixRestructureModal
        open={prefixModalOpen}
        parentNode={selectedNode}
        candidates={prefixCandidates}
        onClose={() => setPrefixModalOpen(false)}
        onConfirm={handleRestructureByPrefix}
      />
      
      </EditorContextProvider>
    </div>
  );
};

const GroupEditor: React.FC<GroupEditorProps> = ({ localKey, onFetchMembers, icons, ...props }) => {
  return (
    <ReactFlowProvider>
        <InnerImplementation 
            {...props} 
            groupData={props.groupData} 
            onFetchMembers={onFetchMembers} 
            icons={icons} 
        />
    </ReactFlowProvider>
  );
};

export default GroupEditor;
